import {
  AssistantChatDialog,
  type AssistantChatSidebarState,
} from "@assetwatch/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetCustomerContext } from "../queries/useGetCustomerContext";
import type { AskReliCustomerChatProps } from "./AskReli.types";
import { EnterpriseCustomerSelectionCard } from "./EnterpriseCustomerState";
import { ReliAssistantPanel } from "./ReliAssistantPanel";

interface AskReliChatDialogProps {
  open: boolean;
  externalCustomerId?: string;
  onClose: () => void;
}

export function AskReliChatDialog({
  open,
  externalCustomerId,
  onClose,
}: AskReliChatDialogProps) {
  const { t } = useTranslation();
  const [minimized, setMinimized] = useState(false);
  const [sidebar, setSidebar] =
    useState<AssistantChatSidebarState>("collapsed");

  return (
    <AssistantChatDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      aria-label={t("enterprise.reliability.assistant.chatDialogAria")}
      minimized={minimized}
      sidebar={sidebar}
    >
      <AskReliCustomerChat
        open={open}
        externalCustomerId={externalCustomerId}
        onClose={onClose}
        minimized={minimized}
        onMinimizedChange={setMinimized}
        sidebar={sidebar}
        onSidebarChange={setSidebar}
      />
    </AssistantChatDialog>
  );
}

function AskReliCustomerChat({
  open,
  externalCustomerId,
  onClose,
  minimized,
  onMinimizedChange,
  sidebar,
  onSidebarChange,
}: AskReliCustomerChatProps & {
  open: boolean;
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  sidebar: AssistantChatSidebarState;
  onSidebarChange: (sidebar: AssistantChatSidebarState) => void;
}) {
  const { t } = useTranslation();

  if (!externalCustomerId) {
    return (
      <EnterpriseCustomerSelectionCard
        description={t(
          "enterprise.reliability.assistant.fallbackSelectCustomerCard",
        )}
      />
    );
  }

  return (
    <CustomerChat
      open={open}
      externalCustomerId={externalCustomerId}
      onClose={onClose}
      minimized={minimized}
      onMinimizedChange={onMinimizedChange}
      sidebar={sidebar}
      onSidebarChange={onSidebarChange}
    />
  );
}

function CustomerChat({
  open,
  externalCustomerId,
  onClose,
  minimized,
  onMinimizedChange,
  sidebar,
  onSidebarChange,
}: AskReliCustomerChatProps & {
  open: boolean;
  externalCustomerId: string;
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  sidebar: AssistantChatSidebarState;
  onSidebarChange: (sidebar: AssistantChatSidebarState) => void;
}) {
  const { data: customer } = useGetCustomerContext(externalCustomerId);

  return (
    <ReliAssistantPanel
      open={open}
      customerId={customer.customerId}
      onClose={onClose}
      minimized={minimized}
      onMinimize={() => onMinimizedChange(true)}
      onExpand={() => onMinimizedChange(false)}
      sidebar={sidebar}
      onSidebarToggle={() =>
        onSidebarChange(sidebar === "expanded" ? "collapsed" : "expanded")
      }
    />
  );
}
